import { useState, useEffect, useCallback } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { useNavigate } from 'react-router-dom';
import { TOAST_STATUS, toastActions } from '@leagueplatform/toast-messages';
import { components } from 'api/gen/communication-preferences';
import { ROUTE_PATHS } from 'common/constants/constants';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handle/use-dirty-form-handle.hook';
import { normalizeFieldName } from 'hooks/use-get-communication-preferences/use-get-communication-preferences.hook';
import {
  CommunicationPreferencesUpdatePayload,
  usePatchCommunicationPreferences,
} from 'hooks/use-patch-communication-preferences/use-patch-communication-preferences.hook';

export type CommunicationPreferencesSubmitVariables = {
  [
    key: string
  ]: components['schemas']['communicationMethods']['communicationMethodCode'];
};

export const useSubmitCommunicationPreferences = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { removeDirtyForm } = useDirtyFormHandler(
    formatMessage({ id: 'STR_DOCUMENT_PREFERENCES' }),
  );

  // State for navigation timing
  const [shouldNavigate, setShouldNavigate] = useState(false);

  // Navigate after state changes complete
  useEffect(() => {
    if (shouldNavigate) {
      navigate(ROUTE_PATHS.COMMUNICATION_PREFERENCES);
      setShouldNavigate(false); // Reset the flag
    }
  }, [shouldNavigate, navigate]);

  const handleSuccess = useCallback(() => {
    removeDirtyForm();
    setShouldNavigate(true);
    toastActions.add({
      type: TOAST_STATUS.SUCCESS,
      text: formatMessage(
        { id: 'STR_FORM_SUCCESS' },
        {
          formName: formatMessage({ id: 'STR_DOCUMENT_PREFERENCES' }),
        },
      ),
    });
  }, [removeDirtyForm, formatMessage]);

  const { patchCommunicationPreferences, isLoading } =
    usePatchCommunicationPreferences({
      onSuccess: handleSuccess,
      onError: () => {
        toastActions.add({
          type: TOAST_STATUS.ERROR,
          text: formatMessage(
            { id: 'STR_FORM_ERROR' },
            {
              formName: formatMessage({ id: 'STR_DOCUMENT_PREFERENCES' }),
            },
          ),
        });
      },
    });

  const submitCommunicationPreferences = useCallback(
    (
      communicationPreferencesData: components['schemas']['getCommunicationPreferencesSchema']['data'],
      values: CommunicationPreferencesSubmitVariables,
    ) => {
      if (!communicationPreferencesData) return;

      const allPreferences: CommunicationPreferencesUpdatePayload['data']['attributes']['preferences'] =
        [];

      communicationPreferencesData?.forEach((section) => {
        const { attributes: sectionAttributes } = section;
        const { preferences: sectionPreferences } = sectionAttributes;

        sectionPreferences?.forEach((preference) => {
          const { name, description, availableMethods } = preference.attributes;

          const preferenceFieldKey =
            sectionAttributes.sectionName && name
              ? normalizeFieldName(sectionAttributes.sectionName, name)
              : '';

          const selectedPreferredMethod = values[preferenceFieldKey];

          if (name && description && availableMethods) {
            allPreferences.push({
              id: preference.id,
              name,
              description,
              preferredMethod: {
                communicationMethodCode: selectedPreferredMethod,
              },
              availableMethods: availableMethods?.map((method) => ({
                communicationMethodCode: method.communicationMethodCode,
              })),
            });
          }
        });
      });

      const payload = {
        data: {
          attributes: {
            preferences: allPreferences,
          },
        },
      };

      patchCommunicationPreferences(payload);
    },
    [patchCommunicationPreferences],
  );

  return {
    submitCommunicationPreferences,
    isLoading,
  };
};
